import dayjs from 'dayjs';
import { type TFunction } from 'i18next';
import type * as React from 'react';
import {
  type SearchableField,
  SearchableFieldType,
} from '../../ISearchableField.tsx';
import { formatCustomFilterBarValue } from './formatCustomFilterBarValue.tsx';
import { formatRangeFilterBarValue } from './formatRangeFilterBarValue.tsx';
import { formatSelectFilterBarValue } from './formatSelectFilterBarValue.tsx';
import { truncateFilterBarString } from './truncateFilterBarString.ts';

/**
 * Ritorna il rendering compatto di un valore di filtro da mostrare nel chip
 * (quando il popover è chiuso). Restituisce `null` se non c'è nulla da
 * visualizzare (campo placeholder, valore vuoto, ecc.).
 *
 * Per i range il chiamante deve passare un oggetto `{ from, to }` come `value`.
 */
export function formatFilterBarValue(
  field: SearchableField,
  value: unknown,
  t: TFunction,
): React.ReactNode {
  switch (field.type) {
    case SearchableFieldType.Text:
      return typeof value === 'string' && value !== ''
        ? truncateFilterBarString(value)
        : null;

    case SearchableFieldType.Number:
      return value === null || value === undefined || value === ''
        ? null
        : String(value);

    case SearchableFieldType.Date:
      return value ? dayjs(value as string).format('DD/MM/YYYY') : null;

    case SearchableFieldType.Switch:
      if (value === true) return t('filter-bar.yes', 'Sì');
      if (value === false) return t('filter-bar.no', 'No');
      return null;

    case SearchableFieldType.Select:
    case SearchableFieldType.Radio:
      return formatSelectFilterBarValue(field, value);

    case SearchableFieldType.DateRange:
    case SearchableFieldType.NumberRange: {
      const { from, to } = (value ?? {}) as {
        from?: string | number | null;
        to?: string | number | null;
      };
      return formatRangeFilterBarValue(field, from, to, t);
    }

    case SearchableFieldType.Custom:
      return formatCustomFilterBarValue(field, value, t);

    default: {
      console.warn('[formatFilterBarValue] Tipo non supportato', { field });
      return t('filter-bar.read-only-value', '—');
    }
  }
}
